import { Box, Typography, Paper, Divider, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import BlockIcon from '@material-ui/icons/Block';
import { NotificationBanner } from '@openchoreo/backstage-plugin-react';
import { WizardStepProps } from './types';
import { getEntitlementClaim } from '../../hooks';
import { BindingScope, SCOPE_NAMESPACE } from '../../constants';
import { useSharedStyles } from '../styles';
import { buildScopePath } from './utils';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: 350,
    marginTop: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  summaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  summaryTitle: {
    fontWeight: 600,
  },
  summaryRow: {
    display: 'flex',
    padding: theme.spacing(1.5, 0),
  },
  summaryLabel: {
    width: 120,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  summaryValue: {
    flex: 1,
    minWidth: 0,
    fontWeight: 500,
    overflowWrap: 'anywhere',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  roleMappingsSection: {
    marginTop: theme.spacing(1),
  },
  roleMappingsTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
  mappingRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  mappingRoleColumn: {
    flex: '0 0 30%',
    minWidth: 0,
    fontWeight: 500,
  },
  mappingScopeColumn: {
    flex: '0 0 35%',
    minWidth: 0,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    overflowWrap: 'anywhere',
  },
  mappingConditionsColumn: {
    flex: '0 0 35%',
    minWidth: 0,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    overflowWrap: 'anywhere',
  },
  conditionEntry: {
    marginBottom: theme.spacing(0.5),
    '&:last-child': { marginBottom: 0 },
  },
  conditionActions: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    color: theme.palette.text.primary,
  },
  conditionExpression: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
  },
  mappingTableHeader: {
    display: 'flex',
    padding: theme.spacing(1, 2),
    borderBottom: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  mappingHeaderLabel: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  mappingTableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  effectChip: {
    fontWeight: 600,
  },
  effectAllow: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  effectDeny: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  effectIcon: {
    fontSize: 16,
  },
  bannerSection: {
    marginTop: theme.spacing(2),
  },
}));

interface ReviewStepProps extends WizardStepProps {
  bindingType?: BindingScope;
  namespace?: string;
}

export const ReviewStep = ({
  state,
  userTypes,
  bindingType,
  namespace,
}: ReviewStepProps) => {
  const classes = useStyles();
  const sharedClasses = useSharedStyles();

  const selectedUserTypeInfo = userTypes.find(
    ut => ut.type === state.subjectType,
  );
  const entitlementClaim = getEntitlementClaim(selectedUserTypeInfo);

  const getSubjectDescription = (): string => {
    const typeName = selectedUserTypeInfo?.displayName || state.subjectType;
    return `${typeName} (${entitlementClaim} = "${state.entitlementValue}")`;
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h5" className={classes.title}>
        Review your role binding
      </Typography>

      <Paper variant="outlined" className={classes.summaryCard}>
        <Box className={classes.summaryHeader}>
          <Typography variant="h5" className={classes.summaryTitle}>
            Summary
          </Typography>
        </Box>

        <Box className={classes.summaryRow}>
          <Typography className={classes.summaryLabel}>Name</Typography>
          <Typography className={classes.summaryValue}>
            {state.name ||
              `${
                state.roleMappings[0]?.role || 'binding'
              }-${state.entitlementValue.trim()}`.toLowerCase()}
          </Typography>
        </Box>

        <Box className={classes.summaryRow}>
          <Typography className={classes.summaryLabel}>Subject</Typography>
          <Typography className={classes.summaryValue}>
            {getSubjectDescription()}
          </Typography>
        </Box>

        <Box className={classes.summaryRow}>
          <Typography className={classes.summaryLabel}>Effect</Typography>
          <Box>
            <Chip
              size="small"
              icon={
                state.effect === 'allow' ? (
                  <CheckCircleIcon className={classes.effectIcon} />
                ) : (
                  <BlockIcon className={classes.effectIcon} />
                )
              }
              label={state.effect.toUpperCase()}
              className={`${classes.effectChip} ${
                state.effect === 'allow'
                  ? classes.effectAllow
                  : classes.effectDeny
              }`}
            />
          </Box>
        </Box>

        <Divider className={classes.divider} />

        <Box className={classes.roleMappingsSection}>
          <Typography variant="h5" className={classes.roleMappingsTitle}>
            Role Mappings ({state.roleMappings.length})
          </Typography>
          <Box className={classes.mappingTableContainer}>
            <Box className={classes.mappingTableHeader}>
              <Box className={classes.mappingRoleColumn}>
                <Typography className={classes.mappingHeaderLabel}>
                  Role
                </Typography>
              </Box>
              <Box className={classes.mappingScopeColumn}>
                <Typography className={classes.mappingHeaderLabel}>
                  Scope
                </Typography>
              </Box>
              <Box className={classes.mappingConditionsColumn}>
                <Typography className={classes.mappingHeaderLabel}>
                  Conditions
                </Typography>
              </Box>
            </Box>
            {state.roleMappings.map((rm, idx) => (
              <Box key={idx} className={classes.mappingRow}>
                <Box className={classes.mappingRoleColumn}>
                  <Typography variant="body2">
                    {rm.role}
                    {bindingType === SCOPE_NAMESPACE &&
                      !rm.roleNamespace &&
                      rm.role && (
                        <Chip
                          label="Cluster"
                          size="small"
                          variant="outlined"
                          className={sharedClasses.clusterRoleChip}
                        />
                      )}
                  </Typography>
                </Box>
                <Box className={classes.mappingScopeColumn}>
                  {buildScopePath(rm, bindingType, namespace)}
                </Box>
                <Box className={classes.mappingConditionsColumn}>
                  {rm.conditions.length === 0
                    ? '—'
                    : rm.conditions.map((c, ci) => (
                        <Box key={ci} className={classes.conditionEntry}>
                          <Box className={classes.conditionActions}>
                            {c.actions.join(', ')}
                          </Box>
                          <Box className={classes.conditionExpression}>
                            {c.expression}
                          </Box>
                        </Box>
                      ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      <Box className={classes.bannerSection}>
        <NotificationBanner
          variant="info"
          showIcon
          message={
            <Typography>
              This mapping will{' '}
              <strong>{state.effect === 'allow' ? 'allow' : 'deny'}</strong>{' '}
              {state.subjectType === 'user' ? 'users' : 'services'} with{' '}
              <strong>
                {entitlementClaim}=&quot;{state.entitlementValue}&quot;
              </strong>{' '}
              to perform actions defined by the above role mappings.
            </Typography>
          }
        />
      </Box>
    </Box>
  );
};
